import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Calendar, Clock, Loader2 } from "lucide-react";
import { API, getAuthHeaders } from "@/App";

export default function ScheduledMessagesDialog({ open, onOpenChange, conversationId }) {
  const [items, setItems] = useState([]);
  const [content, setContent] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const fetchAll = async () => {
    if (!conversationId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/messages/scheduled`, {
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const all = await res.json();
        setItems(all.filter((s) => s.conversation_id === conversationId));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAll();
      // Default to "in 1 hour"
      const d = new Date(Date.now() + 60 * 60 * 1000);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      setDate(`${yyyy}-${mm}-${dd}`);
      setTime(`${hh}:${mi}`);
    }
  }, [open, conversationId]);

  const submit = async () => {
    if (!content.trim() || !date || !time) {
      toast.error("Completa el mensaje y la hora");
      return;
    }
    const local = new Date(`${date}T${time}`);
    if (isNaN(local.getTime()) || local.getTime() <= Date.now()) {
      toast.error("La hora debe ser futura");
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/messages/scheduled`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          conversation_id: conversationId,
          content: content.trim(),
          message_type: "text",
          scheduled_for: local.toISOString(),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Error");
      toast.success("Mensaje programado");
      setContent("");
      fetchAll();
    } catch (e) {
      toast.error(e.message || "No se pudo programar");
    } finally {
      setCreating(false);
    }
  };

  const cancel = async (id) => {
    try {
      const res = await fetch(`${API}/messages/scheduled/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error();
      toast.success("Cancelado");
      fetchAll();
    } catch {
      toast.error("No se pudo cancelar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#17212b] border-[#232e3c] text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" /> Mensajes programados
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Programa un mensaje para enviarlo en el futuro automáticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            placeholder="Escribe tu mensaje..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="bg-[#0e1621] border-[#232e3c] text-white"
            rows={3}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-[#0e1621] border-[#232e3c] text-white" />
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="bg-[#0e1621] border-[#232e3c] text-white" />
          </div>
          <Button onClick={submit} disabled={creating} className="w-full bg-blue-600 hover:bg-blue-700">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
            Programar
          </Button>
        </div>

        <div className="border-t border-[#232e3c] pt-3 mt-2 max-h-72 overflow-y-auto">
          <div className="text-xs uppercase text-slate-400 mb-2">Pendientes</div>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-blue-400" /></div>
          ) : items.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">Sin mensajes programados en este chat.</div>
          ) : (
            <ul className="space-y-2">
              {items.map((s) => (
                <li key={s.schedule_id} className="bg-[#0e1621] border border-[#232e3c] rounded-lg p-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm truncate">{s.content}</div>
                    <div className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" />
                      {new Date(s.scheduled_for).toLocaleString()}
                      {s.status === "failed" && <span className="ml-2 text-red-400">falló</span>}
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => cancel(s.schedule_id)} className="text-slate-400 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
